// Copyright 2017 Sorama B.V.

import { bindable, bindingMode, customAttribute, Disposable, DOM, inject, TaskQueue } from "aurelia-framework";
import { Margin, Size } from "controls/plot/IPlotPart";
import { ResizeListener } from "libraries/Layouting/ResizeListener";

@customAttribute("aspect-maximizer", bindingMode.twoWay)
@inject(DOM.Element, ResizeListener, TaskQueue)
export class AspectRatioSizeMaximizer {
    private readonly element: HTMLElement;
    private readonly resizeListener: ResizeListener;
    private readonly taskQueue: TaskQueue;

    private positioningParent: HTMLElement;

    private currentWidth = -1;
    private currentHeight = -1;
    private currentMarginTop = -1;
    private currentMarginBottom = -1;
    private currentMarginLeft = -1;
    private currentMarginRight = -1;

    private disposables: Disposable[] = [];

    @bindable
    public size: Size;

    @bindable
    public margin: Margin;

    @bindable({ defaultValue: true })
    public centerInParent: boolean;

    constructor(element: HTMLElement, resizeListener: ResizeListener, taskQueue: TaskQueue) {
        this.element = element;
        this.resizeListener = resizeListener;
        this.taskQueue = taskQueue;
    }

    public attached(): void {
        // Wait for layouting to finish and register resize listeners
        this.taskQueue.queueTask(() => {
            this.positioningParent = this.getParentElementWithHeight();
            if (!this.positioningParent) return;

            this.disposables = [
                this.resizeListener.elementResizeEvent(this.element, () => this.sizeChanged()),
                this.resizeListener.elementResizeEvent(this.positioningParent, () => this.sizeChanged()),
            ];

            this.sizeChanged();
        });
    }

    public detached(): void {
        while (this.disposables.length > 0) {
            this.disposables.pop().dispose();
        }
    }

    public marginChanged(): void {
        this.sizeChanged();
    }

    public sizeChanged() {
        if (!this.size || !this.positioningParent) {
            return;
        }

        let w: number;
        let h: number;
        const isFullscreen =
            document.fullscreenElement === this.element || document.fullscreenElement === this.element.parentElement;

        if (isFullscreen) {
            w = document.body.offsetWidth - 20;
            h = this.getParentElementRemainingHeight(this.positioningParent) - 20;
        } else {
            w = this.getParentWidth();
            h = this.getParentElementRemainingHeight(this.positioningParent);
        }

        if (this.margin) {
            w = w - this.margin.left - this.margin.right;
            h = h - this.margin.top - this.margin.bottom;
        }

        // Determine size based on the aspect ratio
        const aspectRatio = this.size.width / this.size.height;

        const size1 = new Size(aspectRatio * h, h);
        const size2 = new Size(w, w / aspectRatio);

        const size = size2.height > h ? size1 : size2;

        if (this.margin) {
            size.width += this.margin.left + this.margin.right;
            size.height += this.margin.top + this.margin.bottom;
        }

        // Compute new margins
        let newMarginLeft: number;
        let newMarginRight: number;
        if (this.centerInParent) {
            newMarginLeft = Math.max((w - size.width) / 2, 0);
            newMarginRight = Math.max((w - size.width) / 2, 0);
        } else {
            newMarginLeft = 0;
            newMarginRight = 0;
        }

        let newMarginTop: number;
        let newMarginBottom: number;
        if (isFullscreen) {
            newMarginTop = Math.max((h - size.height) / 2, 0);
            newMarginBottom = 0;
        } else {
            newMarginTop = 0;
            newMarginBottom = 0;
        }

        if (
            this.currentWidth == size.width &&
            this.currentHeight == size.height &&
            this.currentMarginBottom == newMarginBottom &&
            this.currentMarginTop == newMarginTop &&
            this.currentMarginLeft == newMarginLeft &&
            this.currentMarginRight == newMarginRight
        ) {
            return; // No need to update
        }

        // Set new size
        this.element.style.width = size.width + "px";
        // Add 1 pixel slack in the height if possible and if the aspect ratio > 1
        this.element.style.height = size.height + "px";

        // Set new margins
        this.element.style.marginLeft = newMarginLeft + "px";
        this.element.style.marginRight = newMarginRight + "px";
        this.element.style.marginTop = newMarginTop + "px";
        this.element.style.marginBottom = newMarginBottom + "px";

        this.currentWidth = size.width;
        this.currentHeight = size.height;
        this.currentMarginLeft = newMarginLeft;
        this.currentMarginRight = newMarginRight;
        this.currentMarginTop = newMarginTop;
        this.currentMarginBottom = newMarginBottom;
    }

    private getParentWidth(): number {
        const computedStyle = window.getComputedStyle(this.positioningParent);
        const padLeft = computedStyle.getPropertyValue("padding-left");
        const padRight = computedStyle.getPropertyValue("padding-left");

        const margLeft = computedStyle.getPropertyValue("margin-left");
        const margRight = computedStyle.getPropertyValue("margin-left");

        let leftPadding = 0;
        let rightPadding = 0;

        if (padLeft) {
            leftPadding = Number(padLeft.substr(0, padLeft.length - 2));
        }

        if (padRight) {
            rightPadding = Number(padRight.substr(0, padRight.length - 2));
        }

        if (margLeft) {
            leftPadding += Number(margLeft.substr(0, margLeft.length - 2));
        }

        if (margRight) {
            rightPadding += Number(margRight.substr(0, margRight.length - 2));
        }

        const boundRect = this.positioningParent.getBoundingClientRect();
        return boundRect.width - leftPadding - rightPadding;
    }

    private getParentElementWithHeight(): HTMLElement {
        let parent = this.element.parentElement;
        let boundRect = parent.getBoundingClientRect();

        while (parent && boundRect.height === 0) {
            parent = parent.parentElement;
            boundRect = parent.getBoundingClientRect();
        }

        return parent;
    }

    private getParentElementRemainingHeight(parent: HTMLElement): number {
        const boundingBox = parent.getBoundingClientRect();
        let totalSize = boundingBox.height;

        for (let i = 0; i < parent.childElementCount; i++) {
            const elem = <HTMLElement>parent.children.item(i);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (this.isThisElementInOtherElement(elem) || (<any>elem).__resizeElement__) continue;

            const style = window.getComputedStyle(elem);

            totalSize -= elem.offsetHeight + parseInt(style.marginTop) + parseInt(style.marginBottom);
        }

        return totalSize;
    }

    private isThisElementInOtherElement(parent: Element): boolean {
        if (parent === this.element) return true;

        for (let i = 0; i < parent.childElementCount; i++) {
            const child = <Element>parent.childNodes.item(i);
            if (child === this.element) return true;

            if (this.isThisElementInOtherElement(child)) return true;
        }

        return false;
    }
}
